import { useState, useMemo, useCallback, useEffect } from 'react';

// Import helpers
import {
	AUTO_QUALITY,
	getPlayerSettings,
	checkIsAbr,
	getInitialVideoLanguage,
	SUBTITLES_DISABLE,
	getPlayerSubtitles,
	getPlayerProfiles
} from 'helpers/player';

const usePlayerSettings = (player) => {
	const [selectedTrack, setSelectedTrack] = useState(AUTO_QUALITY.id);
	const [selectedLanguage, setSelectedLanguage] = useState('pl');
	const [selectedSubtitle, setSelectedSubtitle] = useState(
		SUBTITLES_DISABLE.value
	);

	const tracks = useMemo(() => getPlayerSettings(player), [player]);

	const subtitles = useMemo(() => getPlayerSubtitles(player), [player]);

	const profiles = useMemo(
		() => getPlayerProfiles({ tracks, selectedLanguage }),
		[tracks, selectedLanguage]
	);

	useEffect(() => {
		const initialLanguage = getInitialVideoLanguage(tracks);
		setSelectedLanguage(initialLanguage);
	}, [tracks]);

	const handleSetSelectedTrack = useCallback(
		(variant) => {
			if (!variant) return null;
			const { id } = variant;
			const isAbr = checkIsAbr(id);
			setSelectedTrack(id);
			player.configure({ abr: { enabled: isAbr } });
			!isAbr && player.selectVariantTrack(variant, true);
		},
		[player]
	);

	const handleSetAudioLanguage = useCallback(
		({ language, value, profiles, role }) => {
			const isAbr = checkIsAbr(selectedTrack);
			const { streamBandwidth } = player.getStats();
			player.selectAudioLanguage(language, role);
			setSelectedLanguage(value);

			if (!isAbr) {
				const track = profiles.find(
					({ bandwidth }) => bandwidth === streamBandwidth
				);

				handleSetSelectedTrack(track);
			}
		},
		// eslint-disable-next-line
		[player, selectedTrack]
	);

	const handleSetSelectedSubtitle = useCallback(
		({ value, language }) => {
			player.setTextTrackVisibility(!!value);
			value && player.selectTextLanguage(language);
			setSelectedSubtitle(value);
		},
		[player]
	);

	return {
		tracks,
		selectedTrack,
		selectedLanguage,
		profiles,
		subtitles,
		selectedSubtitle,
		setSelectedTrack: handleSetSelectedTrack,
		setAudioLanguage: handleSetAudioLanguage,
		setSelectedSubtitle: handleSetSelectedSubtitle
	};
};

export default usePlayerSettings;
